import { useRef, useEffect } from 'react';
import type CurrencyCode from '@alltrails/core/types/CurrencyCode';
import type Plan from '@alltrails/core/types/Plan';
import { logError, executeRecaptcha } from '@alltrails/core';
import useLanguageCode from '@alltrails/shared/hooks/useLanguageCode';
import type { RecurlyError } from '@recurly/recurly-js';
import { ApiError } from '@alltrails/shared/api';
import logPurchaseFailed from '@alltrails/amplitude/events/logPurchaseFailed';
import logPurchaseButtonTapped from '@alltrails/amplitude/events/logPurchaseButtonTapped';
import classNames from 'classnames';
import type Coupon from '@alltrails/types/Coupon';
import ProductType from '@alltrails/amplitude/enums/ProductType';
import type WebProPurchaseLocation from '@alltrails/amplitude/enums/WebProPurchaseLocation';
import { useIntl } from 'react-intl';
import { getPriceTotal } from '../../utils/plan';
import type ProResponse from '../../types/ProResponse';
import styles from './GooglePayButton.module.scss';

type Props = {
  coupon?: Coupon;
  currencyCode: CurrencyCode;
  onGooglePayToken: ({ google_pay_token, recaptcha }: { google_pay_token: string; recaptcha: string }) => void;
  plan: Plan;
  roundedCorners?: boolean;
  fullWidth?: boolean;
  setErrors: (errors: ProResponse['errors']) => void;
  setSubmitting: (submitting: boolean) => void;
  productType: ProductType;
  webProPurchaseLocation: WebProPurchaseLocation;
};

// https://www.figma.com/file/YubcZX08vX2JgZf6jlKglQ/Google-pay-with-Recurly
function GooglePay({
  coupon,
  currencyCode,
  onGooglePayToken,
  plan,
  roundedCorners,
  fullWidth,
  setErrors,
  setSubmitting,
  productType,
  webProPurchaseLocation
}: Props) {
  const ref = useRef<HTMLDivElement>(null);
  const intl = useIntl();
  const languageCode = useLanguageCode();

  useEffect(() => {
    recurly.configure(process.env.NEXT_PUBLIC_RECURLY_KEY);
  }, []);

  useEffect(() => {
    const googlePay = recurly.GooglePay({
      // see https://developers.google.com/pay/api/web/reference/request-objects#ButtonOptions
      buttonOptions: {
        buttonLocale: languageCode,
        buttonType: 'plain',
        buttonSizeMode: 'fill',
        buttonRadius: roundedCorners ? 100 : 4
      },
      currency: currencyCode,
      country: 'US', // this is the merchants country which is always US
      total: String(getPriceTotal(plan, coupon)),
      googleMerchantId: 'BCR2DN4TQTTI56B4',
      requireBillingAddress: true
    });

    function handleReady(button: Node) {
      if (ref.current) {
        ref.current.replaceChildren(button);
      }
    }

    googlePay.on('ready', handleReady);

    googlePay.on('error', (error: RecurlyError) => {
      // Google Pay is not available based upon several user agent strings. This includes the Google Search App on iOS
      // (https://apps.apple.com/us/app/google/id284815942) and the Instagram in app browser which use "GSA" and "Instagram" in the user agent strings
      // respectively. We don't want to hardcode a list here because the list is dynamically pushed down from Google's pay.js and could change. To see
      // the current strings you can grep GSA in pay.js. We'll ignore this error now that we know it is expected.
      //
      // To reproduce this error either navigate to checkout using Google Search App on iOS or set this user agent - Mozilla/5.0 (iPad; CPU OS 16_2
      // like Mac OS X) AppleWebKit/605.1.15 (KHTML, like Gecko) GSA/250.0.505561494 Mobile/15E148 Safari/604.1
      if (error.code !== 'google-pay-not-available') {
        logError(error);
        logPurchaseFailed({ error_code: error.message, payment_method: 'google_pay' });
      }
    });

    googlePay.on('token', async token => {
      try {
        setSubmitting(true);

        const recaptcha = await executeRecaptcha('userPaymentAction');

        await onGooglePayToken({ google_pay_token: token.id, recaptcha });
      } catch (e) {
        logPurchaseFailed({
          ...(e instanceof ApiError ? { error_code: e.data.base[0] } : {}),
          payment_method: 'google_pay'
        });

        if (e instanceof ApiError) {
          setErrors(e.data);
        }

        setSubmitting(false);
      }
    });

    return () => {
      googlePay.off('ready', handleReady);
    };
  }, [coupon, currencyCode, languageCode, plan, setErrors, setSubmitting]);

  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <div
      className={classNames(styles.container, { [styles.fullWidth]: fullWidth })}
      onClick={() => {
        logPurchaseButtonTapped({ payment_method: 'google_pay', product_type: productType, web_purchase_location: webProPurchaseLocation });
      }}
      ref={ref}
    >
      {/* show a dummy button until Recurly renders the real Google Pay button client side */}
      <button
        aria-label={intl.formatMessage({ defaultMessage: 'Pay with Google Pay' })}
        className={classNames(styles.button, { [styles.roundedCorners]: roundedCorners })}
        disabled
        type="button"
      />
    </div>
  );
}

export default GooglePay;
